import type { tCrossSellMeta, tCrossSellSection, tProduct, tProductPreview } from 'src/shared/frontendTypes/product'

export type FrontendSectionId = 'passend' | 'alternativen' | 'serie'

export type FrontendSection = {
  id: FrontendSectionId
  title: string
  products: tProductPreview[]
}

export type SelectedAttributes = Record<string, number | ''>
type TechnicalFacet = keyof tCrossSellMeta
export type CurrentCombinationSelection = {
  key?: string
  options?: number[]
} | null

const TECHNICAL_TAG_REGEX =
  /^cs-(serie|hersteller|farbe|teil)(?:-([0-9]+(?:-[0-9]+)*|base))?:(.+)$/i
const BASE_COMBINATION_KEY = '__base__'

export const normalizeText = (value?: string) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

const getColorMatchValues = (value?: string) => {
  const display = String(value || '').replace(/\s+/g, ' ').trim()
  const values = [
    display,
    display.match(/\bfarbe\s*([0-9]{1,4})\b/i)?.[1],
    display.match(/\(([0-9]{1,4})\)/)?.[1],
    display.match(/^([0-9]{1,4})(?:\b|$)/)?.[1],
  ]

  return Array.from(new Set(values.map(normalizeText).filter(Boolean)))
}

const technicalValuesMatch = (
  facet: TechnicalFacet,
  currentValue?: string,
  previewValue?: string,
) => {
  if (facet !== 'farbe') {
    return normalizeText(currentValue) === normalizeText(previewValue)
  }

  const currentValues = getColorMatchValues(currentValue)
  const previewValues = new Set(getColorMatchValues(previewValue))

  return currentValues.some(value => previewValues.has(value))
}

const toUniqueNumbers = (value?: string | null) => {
  const numbers = String(value || '')
    .split('-')
    .map(entry => Number(entry))
    .filter((entry): entry is number => Number.isInteger(entry))

  return Array.from(new Set(numbers))
}

export const normalizeCombinationKey = (value?: string | null) => {
  const display = String(value || '').trim()
  if (!display || display.toLowerCase() === 'base') return BASE_COMBINATION_KEY

  const normalizedNumbers = toUniqueNumbers(display)
  return normalizedNumbers.length > 0
    ? normalizedNumbers.join('-')
    : BASE_COMBINATION_KEY
}

const matchesCombinationKey = (
  selectedCombinationKey: string,
  tagCombinationKey: string,
) => {
  if (tagCombinationKey === BASE_COMBINATION_KEY) return false

  const selectedOptions = new Set(toUniqueNumbers(selectedCombinationKey))
  const tagOptions = toUniqueNumbers(tagCombinationKey)

  if (selectedOptions.size === 0 || tagOptions.length === 0) return false

  return tagOptions.every(optionId => selectedOptions.has(optionId))
}

const buildSelectedCombinationKey = (
  selectedAttributes: SelectedAttributes,
  combination?: CurrentCombinationSelection,
) => {
  if (combination?.options?.length) {
    return combination.options.join('-')
  }

  const optionIds = Object.values(selectedAttributes)
    .filter((value): value is number => typeof value === 'number')

  return optionIds.length > 0
    ? optionIds.join('-')
    : BASE_COMBINATION_KEY
}

export const buildCurrentTechnicalMeta = (
  product: tProduct,
  technicalTags: string[] | undefined,
  selectedAttributes: SelectedAttributes,
  combination?: CurrentCombinationSelection,
): tCrossSellMeta => {
  const selectedCombinationKey = buildSelectedCombinationKey(selectedAttributes, combination)
  const baseMeta: Partial<Record<TechnicalFacet, string>> = {}
  const specificMeta: Partial<Record<TechnicalFacet, string>> = {}
  const specificMetaStrength: Partial<Record<TechnicalFacet, number>> = {}
  const colorAttribute = product.attributes?.find(attribute => /farbe|color/i.test(attribute.name))

  for (const rawTag of technicalTags || []) {
    const match = normalizeText(rawTag).match(TECHNICAL_TAG_REGEX)
    if (!match?.[1] || !match?.[3]) continue

    const facet = match[1].toLowerCase() as TechnicalFacet
    const value = String(match[3]).trim()
    const combinationKey = normalizeCombinationKey(match[2])

    if (combinationKey === BASE_COMBINATION_KEY) {
      baseMeta[facet] = value
    }

    if (matchesCombinationKey(selectedCombinationKey, combinationKey)) {
      const strength = toUniqueNumbers(combinationKey).length
      const currentStrength = specificMetaStrength[facet] || 0
      if (strength < currentStrength) continue

      specificMeta[facet] = value
      specificMetaStrength[facet] = strength
    }
  }

  const selectedColorOption = colorAttribute
    ?.options.find(option => option.id === selectedAttributes[colorAttribute.id])
    ?.name

  return {
    ...(baseMeta.hersteller || specificMeta.hersteller ? { hersteller: specificMeta.hersteller || baseMeta.hersteller } : {}),
    ...(baseMeta.serie || specificMeta.serie ? { serie: specificMeta.serie || baseMeta.serie } : {}),
    ...(selectedColorOption || baseMeta.farbe || specificMeta.farbe
      ? { farbe: selectedColorOption || specificMeta.farbe || baseMeta.farbe }
      : {}),
    ...(baseMeta.teil || specificMeta.teil ? { teil: specificMeta.teil || baseMeta.teil } : {}),
  }
}

const isSameProductSelection = (
  currentProduct: tProduct,
  currentCombination: CurrentCombinationSelection,
  selectedAttributes: SelectedAttributes,
  preview: tProductPreview,
) => {
  if (preview.id !== currentProduct.id) return false

  if (currentCombination?.key && preview.key) {
    return currentCombination.key === preview.key
  }

  const currentKey = buildSelectedCombinationKey(selectedAttributes, currentCombination)
  const previewKey = normalizeCombinationKey(
    (preview.options || []).length > 0
      ? (preview.options || []).join('-')
      : 'base',
  )

  return currentKey === previewKey
}

const matchesMeta = (
  previewMeta: tCrossSellMeta | undefined,
  currentMeta: tCrossSellMeta,
  facet: TechnicalFacet,
) => {
  const currentValue = normalizeText(currentMeta[facet])
  if (!currentValue) return true

  const previewValue = normalizeText(previewMeta?.[facet])
  if (!previewValue) return false

  return technicalValuesMatch(facet, currentMeta[facet], previewMeta?.[facet])
}

const inferPreviewColor = (
  preview: tProductPreview,
  currentProduct: tProduct,
) => {
  const colorAttribute = currentProduct.attributes?.find(attribute => /farbe|color/i.test(attribute.name))
  const selectedColorOption = colorAttribute
    ?.options.find(option => (preview.options || []).includes(option.id))
    ?.name
    ?.trim()

  if (selectedColorOption) return selectedColorOption

  const haystack = normalizeText([
    ...(preview.attributes || []),
    preview.metaTitle,
    preview.name,
  ].join(' '))

  for (const option of colorAttribute?.options || []) {
    const optionName = normalizeText(option.name)
    if (!optionName) continue
    if (haystack.includes(optionName)) return option.name.trim()
  }

  const colorLine = (preview.attributes || []).find(attribute => /^(farbe|color)\s*:/i.test(attribute))
  const attributeColor = colorLine?.split(':').slice(1).join(':').trim()
  if (attributeColor) return attributeColor

  return String(preview.crossSellMeta?.farbe || '').trim()
}

const inferPreviewMeta = (
  preview: tProductPreview,
  currentProduct: tProduct,
): tCrossSellMeta => {
  const previewText = normalizeText([
    preview.metaTitle,
    preview.name,
    ...(preview.attributes || []),
  ].join(' '))

  const explicitHersteller = String(preview.crossSellMeta?.hersteller || '').trim()
  const explicitSerie = String(preview.crossSellMeta?.serie || '').trim()
  const explicitTeil = String(preview.crossSellMeta?.teil || '').trim()
  const inferredColor = inferPreviewColor(preview, currentProduct)

  const currentSerie = String(
    currentProduct.crossSellTechnicalTags
      ?.map(tag => {
        const match = normalizeText(tag).match(TECHNICAL_TAG_REGEX)
        return match?.[1] === 'serie' ? String(match[3]).trim() : ''
      })
      .find(Boolean)
      || '',
  ).trim()

  const inferredSerie = explicitSerie
    || (currentSerie && previewText.includes(normalizeText(currentSerie)) ? currentSerie : '')

  return {
    ...(explicitHersteller || preview.brandTitel ? { hersteller: explicitHersteller || preview.brandTitel } : {}),
    ...(inferredSerie ? { serie: inferredSerie } : {}),
    ...(explicitTeil ? { teil: explicitTeil } : {}),
    ...(inferredColor ? { farbe: inferredColor } : {}),
  }
}

const decoratePreviewForSection = (
  preview: tProductPreview,
  sectionId: FrontendSectionId,
  currentProduct: tProduct,
) => {
  if (sectionId !== 'alternativen') return preview

  const color = inferPreviewColor(preview, currentProduct)
  if (!color) return preview

  if (normalizeText(preview.metaTitle).includes(normalizeText(color))) return preview

  return {
    ...preview,
    metaTitle: `${preview.metaTitle} - ${color}`,
  }
}

const filterSectionProducts = (
  sectionId: FrontendSectionId,
  products: tProductPreview[],
  currentProduct: tProduct,
  currentCombination: CurrentCombinationSelection,
  selectedAttributes: SelectedAttributes,
  currentMeta: tCrossSellMeta,
  trustServerResult = false,
) => dedupeProducts(products).filter(preview => {
  const previewMeta = inferPreviewMeta(preview, currentProduct)

  if (isSameProductSelection(currentProduct, currentCombination, selectedAttributes, preview)) {
    return false
  }

  if (sectionId === 'alternativen') {
    const matches =
      matchesMeta(previewMeta, currentMeta, 'hersteller')
      && matchesMeta(previewMeta, currentMeta, 'serie')
      && matchesMeta(previewMeta, currentMeta, 'teil')

    if (!matches) return false

    const currentColor = normalizeText(currentMeta.farbe)
    const previewColor = normalizeText(previewMeta.farbe)
    if (currentColor && previewColor && technicalValuesMatch('farbe', currentMeta.farbe, previewMeta.farbe)) {
      return false
    }
  }

  if (sectionId === 'serie') {
    if (trustServerResult) {
      const currentPart = normalizeText(currentMeta.teil)
      const previewPart = normalizeText(previewMeta.teil)

      if (currentPart && previewPart && currentPart === previewPart) {
        return false
      }

      return true
    }

    const matches =
      matchesMeta(previewMeta, currentMeta, 'hersteller')
      && matchesMeta(previewMeta, currentMeta, 'serie')
      && matchesMeta(previewMeta, currentMeta, 'farbe')

    if (!matches) return false

    const currentPart = normalizeText(currentMeta.teil)
    const previewPart = normalizeText(previewMeta.teil)
    if (currentPart && previewPart && currentPart === previewPart) {
      return false
    }
  }

  return true
})

const dedupeProducts = (products: tProductPreview[]) => {
  const bySignature = new Set<string>()
  const unique: tProductPreview[] = []

  for (const product of products) {
    const signature = [
      product.id,
      product.key || '',
      (product.options || []).join('-'),
    ].join(':')

    if (bySignature.has(signature)) continue
    bySignature.add(signature)
    unique.push(product)
  }

  return unique
}

const getSectionTitle = (
  sections: tCrossSellSection[],
  fallback: string,
) => sections.find(section => Boolean(section.titel?.trim()))?.titel?.trim() || fallback

export const buildFrontendSections = (
  sections: tCrossSellSection[] = [],
  currentProduct: tProduct,
  currentCombination: CurrentCombinationSelection,
  selectedAttributes: SelectedAttributes,
  currentMeta: tCrossSellMeta,
  dynamicSerieSection?: tCrossSellSection | null,
  omitStaticSerie = false,
): FrontendSection[] => {
  const baseSections = omitStaticSerie
    ? sections.filter(section => section.art !== 'serie')
    : sections

  const matchingSections = baseSections.filter(section =>
    section.art === 'zubehoer' || section.art === 'ergaenzung',
  )
  const result: FrontendSection[] = []

  const passendProducts = filterSectionProducts(
    'passend',
    matchingSections.flatMap(section => section.products || []),
    currentProduct,
    currentCombination,
    selectedAttributes,
    currentMeta,
  )
  if (passendProducts.length > 0) {
    result.push({
      id: 'passend',
      title: getSectionTitle(matchingSections, 'Passend dazu'),
      products: passendProducts.map(product =>
        decoratePreviewForSection(product, 'passend', currentProduct),
      ),
    })
  }

  const alternativen = baseSections.find(section => section.art === 'alternativen')
  if ((alternativen?.products || []).length > 0) {
    const products = filterSectionProducts(
      'alternativen',
      alternativen?.products || [],
      currentProduct,
      currentCombination,
      selectedAttributes,
      currentMeta,
    )
    if (products.length === 0) {
      // keep looking at the next section
    } else {
      result.push({
        id: 'alternativen',
        title: alternativen?.titel?.trim() || 'Alternativen',
        products: products.map(product =>
          decoratePreviewForSection(product, 'alternativen', currentProduct),
        ),
      })
    }
  }

  const serie = dynamicSerieSection || baseSections.find(section => section.art === 'serie')
  if ((serie?.products || []).length > 0) {
    const products = filterSectionProducts(
      'serie',
      serie?.products || [],
      currentProduct,
      currentCombination,
      selectedAttributes,
      currentMeta,
      Boolean(dynamicSerieSection),
    )
    if (products.length === 0) {
      return result
    }

    result.push({
      id: 'serie',
      title: serie?.titel?.trim() || 'Auch aus dieser Serie',
      products: products.map(product =>
        decoratePreviewForSection(product, 'serie', currentProduct),
      ),
    })
  }

  return result
}

